import React from 'react';
import { useHistory } from 'react-router-dom';
import AppointmentForm from '../components/AppointmentForm';
import MetricCard from '../components/MetricCard';
import RevenueProjection from '../components/RevenueProjection';
import useCalculator from '../hooks/useCalculator';
import { formatCurrency, formatPercentage } from '../utils/formatters';

const Calculator: React.FC = () => {
    const history = useHistory();
    const { formValues, updateField, calculate, results, hasResults } = useCalculator();

    const handleFormSubmit = () => {
        calculate();
    };

    const handleViewResults = () => {
        const latest = results ?? calculate();
        history.push('/results', {
            results: latest,
            inputs: formValues,
        });
    };

    const metricCards = results
        ? [
              {
                  title: 'ROI',
                  value: formatPercentage(results.roi),
                  description: 'Return on your total monthly investment',
              },
              {
                  title: 'Net Profit',
                  value: formatCurrency(results.netProfit),
                  description: 'Revenue after marketing and operating costs',
              },
              {
                  title: 'Total Revenue',
                  value: formatCurrency(results.totalRevenue),
                  description: 'Projected revenue from closed clients',
              },
              {
                  title: 'Cost Per Acquisition',
                  value: results.costPerAcquisition ? formatCurrency(results.costPerAcquisition) : '—',
                  description: 'Average cost to acquire one new client',
              },
          ]
        : [];

    return (
        <section className="calculator-page">
            <h1>ROI Calculator</h1>
            <p className="page-subtitle">
                Model your monthly performance to see how marketing spend, conversion rates, and retention impact ROI.
            </p>
            <div className="calculator-grid">
                <AppointmentForm values={formValues} onFieldChange={updateField} onSubmit={handleFormSubmit} />

                <div className="calculator-results">
                    {hasResults ? (
                        <>
                            <div className="metrics-grid">
                                {metricCards.map((metric) => (
                                    <MetricCard key={metric.title} {...metric} />
                                ))}
                            </div>
                            <RevenueProjection results={results} />
                            <div className="results-actions">
                                <button type="button" className="secondary-button" onClick={handleViewResults}>
                                    View detailed breakdown
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="results-placeholder">
                            <h2>Run the numbers</h2>
                            <p>Complete the form to see ROI, revenue projections, and profitability metrics.</p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Calculator;
